// ===== IMPORTS & DEPENDENCIES =====
<template>
  <span @click="handleDownload">
    <slot></slot>
  </span>
</template>

<script>
import * as XLSX from 'xlsx-js-style'

// ===== CORE BUSINESS LOGIC =====
export default {
  name: 'DownloadExcel',
  props: {
    beforeGenerate: {
      type: Function,
      default: () => {}
    },
    beforeFinish: {
      type: Function,
      default: () => {}
    },
    type: {
      type: String,
      default: 'xlsx'
    },
    name: {
      type: String,
      default: 'download.xlsx'
    },
    fields: {
      type: Object,
      default: () => ({})
    },
    data: {
      type: Array,
      default: () => []
    },
    worksheet: {
      type: String,
      default: 'Sheet1'
    },
    headerStyle: {
      type: Object,
      default: () => ({
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4472C4' } },
        alignment: { horizontal: 'center', vertical: 'center' }
      })
    },
    columnWidths: {
      type: Array,
      default: () => [] // [20, 15, 30] sets widths for columns A, B, C
    },
    enableStyling: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    async handleDownload() {
      try {
        // Execute before-generate callback FIRST (may be async)
        const result = this.beforeGenerate()

        // Check if beforeGenerate returns a Promise (async function)
        if (result && typeof result.then === 'function') {
          console.log('Waiting for async beforeGenerate...')
          await result
        }

        // Wait for next tick to ensure all data is properly set
        this.$nextTick(() => {
          this.performExport()
        })
      } catch (error) {
        console.error('Excel export error:', error)
        this.beforeFinish()
      }
    },

    performExport() {
      try {
        // Debug logs
        console.log('Raw data:', this.data)
        console.log('Fields mapping:', this.fields)

        // Transform data to match field mappings
        const transformedData = this.transformData()
        console.log('Transformed data:', transformedData)

        // Validate we have data to export
        if (!transformedData || transformedData.length === 0) {
          console.warn('No data to export!')
          this.beforeFinish()
          return
        }

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(transformedData)

        // Check if xlsx-js-style is loaded correctly
        console.log('XLSX version:', XLSX.version)
        console.log('XLSX has writeFileXLSX:', typeof XLSX.writeFileXLSX)

        // Apply styling if enabled
        if (this.enableStyling) {
          this.applyExcelStyling(ws, transformedData)
        }

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, this.worksheet)

        // Force .xlsx format for all Excel exports (ignore 'xls' type)
        const extension = this.type === 'csv' ? 'csv' : 'xlsx'
        let filename = this.name

        // Replace .xls extension with .xlsx, or add .xlsx if no extension
        if (filename.endsWith('.xls')) {
          filename = filename.replace('.xls', '.xlsx')
        } else if (!filename.includes('.')) {
          filename = `${filename}.${extension}`
        }

        // Write and download file - always use xlsx format unless CSV
        XLSX.writeFile(wb, filename, {
          bookType: this.type === 'csv' ? 'csv' : 'xlsx'
        })

        // Execute before-finish callback (matches vue-json-excel behavior)
        this.beforeFinish()
      } catch (error) {
        console.error('Excel export error in performExport:', error)
        this.beforeFinish()
      }
    },

    transformData() {
      if (!this.data || !Array.isArray(this.data)) {
        return []
      }

      // If no fields mapping provided, return data as-is
      if (!this.fields || Object.keys(this.fields).length === 0) {
        return this.data
      }

      // Transform data using fields mapping (label -> property)
      return this.data.map((row) => {
        const transformedRow = {}

        Object.entries(this.fields).forEach(([label, property]) => {
          // Handle nested properties (e.g., 'user.name')
          transformedRow[label] = this.getNestedValue(row, property)
        })

        return transformedRow
      })
    },

    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : ''
      }, obj)
    },

    applyExcelStyling(worksheet, data) {
      try {
        console.log('Applying Excel styling...')

        // Set column widths
        if (this.columnWidths.length > 0) {
          worksheet['!cols'] = this.columnWidths.map((width) => ({
            wch: width
          }))
          console.log('Column widths applied:', this.columnWidths)
        }

        // Apply header styling - ensure we have data first
        if (data && data.length > 0) {
          const headers = Object.keys(data[0])
          console.log('Headers found:', headers)

          headers.forEach((_, index) => {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index })
            console.log('Styling cell:', cellAddress)

            if (!worksheet[cellAddress]) {
              worksheet[cellAddress] = { t: 's', v: headers[index] }
            }

            // Use custom header style if provided, otherwise use default
            const styleToApply = this.headerStyle || {
              font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
              fill: { fgColor: { rgb: '4472C4' } },
              alignment: { horizontal: 'center', vertical: 'center' }
            }

            console.log(
              'Style being applied to',
              cellAddress,
              ':',
              styleToApply
            )
            worksheet[cellAddress].s = styleToApply
          })
        }

        // Set row height for header
        if (!worksheet['!rows']) worksheet['!rows'] = []
        worksheet['!rows'][0] = { hpt: 25, hpx: 25 }

        console.log('Styling applied successfully')
      } catch (error) {
        console.error('Error applying Excel styling:', error)
      }
    },

    // Helper method to create custom header styles
    createHeaderStyle(options = {}) {
      return {
        font: {
          bold: true,
          color: { rgb: options.fontColor || 'FFFFFF' },
          size: options.fontSize || 12
        },
        fill: {
          fgColor: { rgb: options.backgroundColor || '4472C4' }
        },
        alignment: {
          horizontal: options.textAlign || 'center',
          vertical: 'center'
        },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      }
    }
  }
}
</script>
